import React, {useRef} from "react";
import {
    Code2,
    Bold,
    Highlighter,
    Plus,
    Trash2,
} from "lucide-react";
import ImageUpload from "../../../../components/elements/ImageUpload";

const LANGUAGES = [
    "python",
    "javascript",
    "java",
    "cpp",
    "c",
    "csharp",
    "go",
    "rust",
    "php",
];

export default function BlockEditorPanel({
                                             fragmentId,
                                             block,
                                             onChange,
                                             onOpenAdvancedJson,
                                         }) {
    if (!fragmentId)
        return <div className="text-gray-500">یک فرگمنت انتخاب کن</div>;
    if (!block) return <div className="text-gray-500">یک بلاک انتخاب کن</div>;

    const set = (patch) => onChange(patch);
    const editorRef = useRef(null);

    const applyFormat = (command) => {
        if (command === "highlight") {
            document.execCommand("hiliteColor", false, "yellow");
        } else {
            document.execCommand(command, false, null);
        }

        const html = editorRef.current?.innerHTML || "";

        const normalized = html
            .replace(/<strong>/g, "<b>")
            .replace(/<\/strong>/g, "</b>")
            .replace(/<span style="background-color: yellow;">/g, "<h>")
            .replace(/<\/span>/g, "</h>");

        set({
            config: {
                ...block.config,
                text: normalized,
            },
        });
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold">ویرایش بلاک</h2>
                <button
                    onClick={onOpenAdvancedJson}
                    className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-50 text-sm flex items-center gap-2"
                >
                    <Code2 size={16}/> JSON
                </button>
            </div>

            {/* ================= TEXT ================= */}
            {block.type === "text" && (
                <div className="space-y-3">
                    <div className="flex gap-2">
                        <button
                            onClick={() => applyFormat("bold")}
                            className="p-2 border rounded"
                        >
                            <Bold size={16}/>
                        </button>

                        <button
                            onClick={() => applyFormat("highlight")}
                            className="p-2 border rounded"
                        >
                            <Highlighter size={16}/>
                        </button>
                    </div>

                    <div
                        ref={editorRef}
                        dir="rtl"
                        contentEditable
                        suppressContentEditableWarning
                        onInput={(e) =>
                            set({
                                config: {
                                    ...block.config,
                                    text: e.currentTarget.innerHTML,
                                },
                            })
                        }
                        dangerouslySetInnerHTML={{
                            __html: block.config.text || "",
                        }}
                        className="w-full min-h-[220px] p-3 border rounded-lg bg-gray-50 outline-none"
                    />
                </div>
            )}

            {/* ================= HEADING ================= */}
            {block.type === "heading" && (
                <div className="space-y-2">
                    <input
                        value={block.config.text}
                        onChange={(e) => set({config: {...block.config, text: e.target.value}})}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                        placeholder="عنوان"
                    />

                    <select
                        value={block.config.level}
                        onChange={(e) => set({config: {...block.config, level: Number(e.target.value)}})}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    >
                        {[1, 2, 3, 4, 5, 6].map(l => (
                            <option key={l} value={l}>H{l}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* ================= IMAGE ================= */}
            {block.type === "image" && (
                <div className="space-y-2">
                    <ImageUpload
                        name="Image"
                        existingImage={block.config.src}
                        onFileChange={(e) => set({config: {...block.config, src: e.target.value}})}
                    />
                </div>
            )}

            {/* ================= CODE ================= */}
            {block.type === "code" && (
                <div className="space-y-3">
                    <select
                        value={block.config.language}
                        onChange={(e) => set({config: {...block.config, language: e.target.value}})}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    >
                        {LANGUAGES.map((l) => (
                            <option key={l} value={l}>{l}</option>
                        ))}
                    </select>

                    <textarea
                        value={block.config.code}
                        onChange={(e) => set({config: {...block.config, code: e.target.value}})}
                        rows={6}
                        className="w-full p-3 border rounded-lg bg-gray-50 font-mono"
                    />

                    <label className="flex gap-2 items-center">
                        <input
                            type="checkbox"
                            checked={block.config.editable}
                            onChange={(e) => set({config: {...block.config, editable: e.target.checked}})}
                        />
                        قابل ویرایش
                    </label>

                    <label className="flex gap-2 items-center">
                        <input
                            type="checkbox"
                            checked={block.config.showCompiler}
                            onChange={(e) => set({config: {...block.config, showCompiler: e.target.checked}})}
                        />
                        نمایش کامپایلر
                    </label>

                    <textarea
                        placeholder="نتیجه مورد انتظار"
                        value={block.config.result.expected}
                        onChange={(e) =>
                            set({
                                config: {
                                    ...block.config,
                                    result: {...block.config.result, expected: e.target.value},
                                },
                            })
                        }
                        className="w-full p-3 border rounded-lg bg-gray-50 font-mono"
                        rows={3}
                    />

                    <input
                        placeholder="هینت"
                        value={block.config.hint}
                        onChange={(e) => set({config: {...block.config, hint: e.target.value}})}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    />
                </div>
            )}

            {/* ================= SINGLE CHOICE ================= */}
            {block.type === "single_choice" && (
                <div className="space-y-3">
        <textarea
            placeholder="سوال"
            value={block.config.prompt}
            onChange={(e) =>
                set({
                    config: {
                        ...block.config,
                        prompt: e.target.value,
                    },
                })
            }
            className="w-full p-3 border rounded-lg bg-gray-50"
        />

                    {block.config.options.map((opt, i) => (
                        <div key={opt.id} className="flex gap-2 items-center">
                            <input
                                type="radio"
                                checked={block.config.correctOptionId === opt.id}
                                onChange={() =>
                                    set({
                                        config: {
                                            ...block.config,
                                            correctOptionId: opt.id,
                                        },
                                    })
                                }
                            />

                            <input
                                value={opt.text}
                                onChange={(e) => {
                                    const options = [...block.config.options];
                                    options[i].text = e.target.value;

                                    set({
                                        config: {
                                            ...block.config,
                                            options,
                                        },
                                    });
                                }}
                                className="flex-1 p-2 border rounded"
                            />

                            <button
                                onClick={() => {
                                    const options = block.config.options.filter(
                                        (o) => o.id !== opt.id
                                    );

                                    set({
                                        config: {
                                            ...block.config,
                                            options,
                                        },
                                    });
                                }}
                            >
                                <Trash2 size={16}/>
                            </button>
                        </div>
                    ))}

                    <button
                        onClick={() =>
                            set({
                                config: {
                                    ...block.config,
                                    options: [
                                        ...block.config.options,
                                        {
                                            id: crypto.randomUUID(),
                                            text: "",
                                        },
                                    ],
                                },
                            })
                        }
                        className="flex gap-2 text-blue-600"
                    >
                        <Plus size={16}/>
                        افزودن گزینه
                    </button>
                </div>
            )}

            {/* ================= MULTIPLE CHOICE ================= */}
            {block.type === "multiple_choice" && (
                <div className="space-y-3">
        <textarea
            placeholder="سوال"
            value={block.config.prompt}
            onChange={(e) =>
                set({
                    config: {
                        ...block.config,
                        prompt: e.target.value,
                    },
                })
            }
            className="w-full p-3 border rounded-lg bg-gray-50"
        />

                    {block.config.options.map((opt, i) => (
                        <div key={opt.id} className="flex gap-2 items-center">
                            <input
                                type="radio"
                                checked={block.config.correctOptionId === opt.id}
                                onChange={() =>
                                    set({
                                        config: {
                                            ...block.config,
                                            correctOptionId: opt.id,
                                        },
                                    })
                                }
                            />

                            <input
                                value={opt.text}
                                onChange={(e) => {
                                    const options = [...block.config.options];
                                    options[i].text = e.target.value;

                                    set({
                                        config: {
                                            ...block.config,
                                            options,
                                        },
                                    });
                                }}
                                className="flex-1 p-2 border rounded"
                            />

                            <button
                                onClick={() => {
                                    const options = block.config.options.filter(
                                        (o) => o.id !== opt.id
                                    );

                                    set({
                                        config: {
                                            ...block.config,
                                            options,
                                        },
                                    });
                                }}
                            >
                                <Trash2 size={16}/>
                            </button>
                        </div>
                    ))}

                    <button
                        onClick={() =>
                            set({
                                config: {
                                    ...block.config,
                                    options: [
                                        ...block.config.options,
                                        {
                                            id: crypto.randomUUID(),
                                            text: "",
                                        },
                                    ],
                                },
                            })
                        }
                        className="flex gap-2 text-blue-600"
                    >
                        <Plus size={16}/>
                        افزودن گزینه
                    </button>
                </div>
            )}

            {/* ================= PLACEHOLDER CODE ================= */}
            {block.type === "placeholder_code" && (
                <div className="space-y-4">
                    <select
                        value={block.config.language}
                        onChange={(e) =>
                            set({
                                config: {
                                    ...block.config,
                                    language: e.target.value,
                                },
                            })
                        }
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    >
                        {LANGUAGES.map((l) => (
                            <option key={l}>{l}</option>
                        ))}
                    </select>

                    <textarea
                        rows={8}
                        value={block.config.template.join("\n")}
                        onChange={(e) =>
                            set({
                                config: {
                                    ...block.config,
                                    template: e.target.value.split("\n"),
                                },
                            })
                        }
                        className="w-full p-3 border rounded-lg bg-gray-50 font-mono"
                    />

                    {block.config.placeholders.map((p, i) => (
                        <div key={p.key} className="space-y-2 border p-3 rounded">
                            <input
                                value={p.key}
                                disabled
                                className="w-full p-2 border rounded bg-gray-100"
                            />

                            <input
                                placeholder="starter"
                                value={p.starter}
                                onChange={(e) => {
                                    const placeholders = [
                                        ...block.config.placeholders,
                                    ];

                                    placeholders[i].starter =
                                        e.target.value;

                                    set({
                                        config: {
                                            ...block.config,
                                            placeholders,
                                        },
                                    });
                                }}
                                className="w-full p-2 border rounded"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
